/**
 * Mock service to simulate data processing on the backend
 */

/**
 * Process the data and return processed data with classifications
 * @param data The raw data to process
 * @param progressCallback Callback function to update progress
 * @param selectedColumns Array of column names to focus on for processing
 * @param customSteps Optional array of custom step names to use instead of defaults
 * @returns Promise that resolves to processed data and classifications
 */
export const processData = (
  data: any[],
  progressCallback: (progress: number, step: string) => void,
  selectedColumns: string[] = [],
  customSteps?: string[]
): Promise<{ processedData: any[], classifications: any[] }> => {
  return new Promise((resolve) => {
    // Default processing steps with exact 2.5 second duration each
    const defaultSteps = [
      { name: 'Validating data structure...', duration: 2500 },
      { name: 'Cleansing data...', duration: 2500 },
      { name: 'Standardizing descriptions...', duration: 2500 },
      { name: 'Classifying items...', duration: 2500 }
    ];
    
    // Use custom steps if provided, otherwise use defaults
    // Ensure exactly 4 steps for a total of 10 seconds (4 * 2.5s)
    const steps = customSteps 
      ? customSteps.slice(0, 4).map(name => ({
          name,
          duration: 2500 // Exactly 2.5 seconds per step
        }))
      : defaultSteps;
    
    // Ensure we have exactly 4 steps
    while (steps.length < 4) {
      steps.push({ 
        name: defaultSteps[steps.length % defaultSteps.length].name, 
        duration: 2500 
      });
    }
    
    const totalDuration = 10000; // Exactly 10 seconds total
    let elapsedTime = 0;
    
    // Find the supplier and item columns once for all items
    const supplierColumn = selectedColumns.find(col => 
      col.toLowerCase().includes('supplier') || col.toLowerCase().includes('vendor')
    );
    
    const itemColumn = selectedColumns.find(col => 
      col.toLowerCase().includes('item') || 
      col.toLowerCase().includes('description') || 
      col.toLowerCase().includes('name')
    );
    
    // Process each step sequentially
    const processStep = (index: number) => {
      if (index >= steps.length) {
        // All steps completed
        progressCallback(100, 'Processing complete');
        
        // Return the processed data with focus on selected columns
        const processedData = data.map(item => {
          const processedItem = { ...item };
          
          // Extract supplier and item information from selected columns
          const supplierInfo = supplierColumn ? item[supplierColumn] : 'Unknown Supplier';
          const itemInfo = itemColumn ? item[itemColumn] : 'Unknown Item';
          
          // Add processed fields
          processedItem.standardizedDescription = itemInfo ? String(itemInfo).toUpperCase() : '';
          processedItem.supplierCategory = getSupplierCategory(supplierInfo);
          processedItem.itemCategory = getItemCategory(itemInfo);
          processedItem.itemSubcategory = getItemSubcategory(itemInfo);
          processedItem.confidence = calculateConfidence(selectedColumns.length);
          processedItem.potentialSavings = calculateSavings(item, processedItem.itemCategory);
          processedItem.riskScore = calculateRiskScore(supplierInfo, itemInfo);
          
          return processedItem;
        });
        
        // Extract classifications for reporting
        const classifications = processedData.map(item => ({
          id: item.id,
          description: itemColumn ? item[itemColumn] : 'Unknown Item',
          supplier: supplierColumn ? item[supplierColumn] : 'Unknown Supplier',
          category: item.itemCategory,
          subcategory: item.itemSubcategory,
          confidence: item.confidence
        }));
        
        resolve({ processedData, classifications });
        return;
      }
      
      const step = steps[index];
      // Calculate progress based on current step (25% per step)
      const progress = Math.min(25 * index, 99);
      
      progressCallback(
        progress,
        step.name
      );
      
      // Each step takes exactly 2.5 seconds
      setTimeout(() => {
        elapsedTime += 2500;
        processStep(index + 1);
      }, 2500);
    };
    
    // Start processing
    processStep(0);
  });
};

// Helper functions for classification

function getSupplierCategory(supplierInfo: any): string {
  const supplierStr = String(supplierInfo || '').toLowerCase();
  
  if (supplierStr.includes('tech') || supplierStr.includes('it') || supplierStr.includes('computer')) {
    return 'IT Vendor';
  } else if (supplierStr.includes('office') || supplierStr.includes('supply')) {
    return 'Office Supplies';
  } else if (supplierStr.includes('consult') || supplierStr.includes('service')) {
    return 'Professional Services';
  } else if (supplierStr.includes('market') || supplierStr.includes('media')) {
    return 'Marketing';
  } else if (supplierStr.includes('travel') || supplierStr.includes('hotel')) {
    return 'Travel';
  } else {
    return 'Other';
  }
}

function getItemCategory(itemInfo: any): string {
  const itemStr = String(itemInfo || '').toLowerCase();
  
  if (itemStr.includes('laptop') || itemStr.includes('computer') || itemStr.includes('server')) {
    return 'IT Equipment';
  } else if (itemStr.includes('paper') || itemStr.includes('pen') || itemStr.includes('desk')) {
    return 'Office Supplies';
  } else if (itemStr.includes('consult') || itemStr.includes('service') || itemStr.includes('support')) {
    return 'Professional Services';
  } else if (itemStr.includes('travel') || itemStr.includes('flight') || itemStr.includes('hotel')) {
    return 'Travel';
  } else if (itemStr.includes('ad') || itemStr.includes('marketing') || itemStr.includes('campaign')) {
    return 'Marketing';
  } else {
    return 'Miscellaneous';
  }
}

function getItemSubcategory(itemInfo: any): string {
  const itemStr = String(itemInfo || '').toLowerCase();
  const category = getItemCategory(itemInfo);
  
  switch (category) {
    case 'IT Equipment':
      if (itemStr.includes('laptop')) return 'Laptops';
      if (itemStr.includes('server')) return 'Servers';
      if (itemStr.includes('monitor')) return 'Monitors';
      return 'Other IT';
      
    case 'Office Supplies':
      if (itemStr.includes('paper')) return 'Paper Products';
      if (itemStr.includes('pen') || itemStr.includes('marker')) return 'Writing Instruments';
      if (itemStr.includes('furniture')) return 'Furniture';
      return 'General Office Supplies';
      
    case 'Professional Services':
      if (itemStr.includes('consult')) return 'Consulting';
      if (itemStr.includes('legal')) return 'Legal Services';
      if (itemStr.includes('accounting')) return 'Accounting Services';
      return 'Other Services';
      
    default:
      return 'General';
  }
}

function calculateConfidence(numSelectedColumns: number): number {
  // More selected columns = higher confidence (up to 95%)
  const baseConfidence = 70;
  const columnBonus = Math.min(numSelectedColumns * 5, 25);
  return baseConfidence + columnBonus;
}

function calculateSavings(item: any, category: string): number {
  // Mock calculation based on item amount and category
  const amount = parseFloat(item['Order amount'] || item['Amount'] || '0');
  
  if (isNaN(amount)) return 0;
  
  const savingsRates: Record<string, number> = {
    'IT Equipment': 0.15,
    'Office Supplies': 0.25,
    'Professional Services': 0.10,
    'Travel': 0.20,
    'Marketing': 0.12,
    'Miscellaneous': 0.05
  };
  
  const rate = savingsRates[category] || 0.05;
  return amount * rate;
}

function calculateRiskScore(supplierInfo: any, itemInfo: any): number {
  // Mock risk score calculation (0-100)
  const supplierStr = String(supplierInfo || '').toLowerCase();
  const itemStr = String(itemInfo || '').toLowerCase();
  
  let score = 50; // Base score
  
  // Supplier factors
  if (supplierStr.includes('new') || supplierStr.length < 5) {
    score += 20; // New or unknown suppliers are higher risk
  }
  
  if (supplierStr.includes('preferred') || supplierStr.includes('partner')) {
    score -= 15; // Preferred suppliers are lower risk
  }
  
  // Item factors
  if (itemStr.includes('custom') || itemStr.includes('special')) {
    score += 10; // Custom items are higher risk
  }
  
  if (itemStr.includes('standard') || itemStr.includes('basic')) {
    score -= 10; // Standard items are lower risk
  }
  
  // Ensure score is between 0-100
  return Math.max(0, Math.min(100, score));
} 